import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { customerAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useDialog } from '../../components/Dialog';
import './CustomerFormPage.css';

const CustomerFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showSuccess, showError, showConfirm, DialogComponent } = useDialog();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    idType: 'cccd',
    idNumber: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    gender: 'male',
    address: '',
    country: 'Vietnam',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await customerAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      showError(t('cannotLoadCustomer'));
      setTimeout(() => navigate('/loan/customers'), 1500);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('enterName');
    }

    if (!formData.idNumber.trim()) {
      newErrors.idNumber = t('enterIdNumber');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('enterPhone');
    } else if (!/^[+]?[0-9]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = t('invalidPhone');
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        await customerAPI.update(id, formData);
        showSuccess(t('customerUpdated'));
      } else {
        await customerAPI.create(formData);
        showSuccess(t('customerCreated'));
      }
      setTimeout(() => navigate('/loan/customers'), 1500);
    } catch (error) {
      console.error('Failed to save customer:', error);
      showError(error.response?.data?.message || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    showConfirm(
      language === 'vi' ? 'Xoá khách hàng' : language === 'ko' ? '고객 삭제' : 'Delete Customer',
      async () => {
        setDeleteLoading(true);

        try {
          await api.post(`/ctv/customers/${id}/hide`);
          showSuccess(language === 'vi' ? 'Đã xoá khách hàng' : language === 'ko' ? '고객이 삭제되었습니다' : 'Customer deleted');
          setTimeout(() => navigate('/loan/customers'), 1500);
        } catch (error) {
          console.error('Failed to delete customer:', error);
          showError(error.response?.data?.message || (language === 'vi' ? 'Không thể xoá khách hàng' : language === 'ko' ? '고객을 삭제할 수 없습니다' : 'Cannot delete customer'));
        } finally {
          setDeleteLoading(false);
        }
      }
    );
  };

  return (
    <div className="customer-form-page">
      <DialogComponent />
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/loan/customers')}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">
          {isEdit ? t('editCustomer') : t('addCustomer')}
        </h1>
      </div>

      <div className="page-content">
        <form onSubmit={handleSubmit} className="customer-form">
          <div className="form-section">
            <h2 className="section-title">{t('personalInfo')}</h2>

            <div className="form-group">
              <label className="form-label">
                {t('customerName')} <span className="required">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('customerName')}
              />
              {errors.name && <p className="form-error">{errors.name}</p>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  {t('idTypeLabel')} <span className="required">*</span>
                </label>
                <select name="idType" value={formData.idType} onChange={handleChange}>
                  <option value="cmnd">CMND</option>
                  <option value="cccd">CCCD</option>
                  <option value="passport">Passport</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('idNumberLabel')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  placeholder="001234567890"
                />
                {errors.idNumber && <p className="form-error">{errors.idNumber}</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  {t('phone')} <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+84 912 345 678"
                />
                {errors.phone && <p className="form-error">{errors.phone}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                />
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('dateOfBirthLabel')}</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('gender')}</label>
                <select name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="male">{t('male')}</option>
                  <option value="female">{t('female')}</option>
                  <option value="other">{t('other')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">{t('addressSection')}</h2>

            <div className="form-group">
              <label className="form-label">{t('address')}</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder={t('address')}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('country')}</label>
              <select name="country" value={formData.country} onChange={handleChange}>
                <option value="Vietnam">Vietnam</option>
                <option value="Korea">Korea</option>
                <option value="USA">USA</option>
                <option value="Japan">Japan</option>
                <option value="China">China</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">{t('notesSection')}</h2>

            <div className="form-group">
              <label className="form-label">{t('notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder={t('notes')}
                rows="4"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/loan/customers')}
            >
              {t('cancel')}
            </button>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-sm"></span>
                  {isEdit ? t('updating') : t('creating')}
                </>
              ) : (
                <>
                  <Save size={18} />
                  {isEdit ? t('update') : t('create')}
                </>
              )}
            </button>

            {isEdit && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <span className="spinner-sm"></span>
                    {language === 'vi' ? 'Đang xoá...' : language === 'ko' ? '삭제 중...' : 'Deleting...'}
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    {language === 'vi' ? 'Xoá' : language === 'ko' ? '삭제' : 'Delete'}
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerFormPage;
